import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useCallback, useEffect, } from "react";
import Button from "../base/Button";
const ButtonDropdown = React.forwardRef(({ children, items, onSelect, variant = "primary", disabled = false, className = "", rounded = false, ...props }, forwardedRef) => {
    const [isOpen, setIsOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);
    const listRef = useRef(null);
    const baseClassNames = {
        container: `relative inline-block ${className}`,
        dropdownContainer: "absolute right-0 z-[2000] min-w-[240px] whitespace-nowrap",
        dropdown: `
        min-w-full bg-white border border-gray-300 rounded-lg shadow-lg 
        overflow-hidden mt-2
      `,
        itemsList: "max-h-[200px] overflow-y-auto list-none p-2 m-0",
        item: (isSelected, isFocused) => `
        rounded-lg px-3 py-3 text-base font-medium flex justify-between cursor-pointer
        ${isSelected || isFocused ? "bg-idk-gradient" : "hover:bg-gray-50"}
        transition-colors duration-150 ease-in-out
      `,
        error: "flex items-center gap-1 mt-2.5 text-red-600 text-sm font-medium",
    };
    useEffect(() => {
        if (!buttonRef.current)
            return;
        if (typeof forwardedRef === "function") {
            forwardedRef(buttonRef.current);
        }
        else if (forwardedRef) {
            forwardedRef.current = buttonRef.current;
        }
    }, [forwardedRef]);
    const handleButtonClick = useCallback((e) => {
        e.preventDefault();
        if (!disabled) {
            setIsOpen((prev) => !prev);
        }
    }, [disabled]);
    const handleOptionClick = useCallback((option) => {
        onSelect(option.value);
        setIsOpen(false);
        setFocusedIndex(-1);
    }, [onSelect]);
    const handleKeyDown = useCallback((e) => {
        switch (e.key) {
            case "Enter":
                e.preventDefault();
                if (disabled)
                    return;
                if (!isOpen) {
                    setIsOpen(true);
                }
                else if (focusedIndex >= 0) {
                    handleOptionClick(items[focusedIndex]);
                }
                break;
            case "ArrowDown":
                e.preventDefault();
                if (disabled)
                    return;
                if (!isOpen) {
                    setIsOpen(true);
                }
                else {
                    setFocusedIndex((prev) => Math.min(prev + 1, items.length - 1));
                }
                break;
            case "ArrowUp":
                e.preventDefault();
                if (isOpen) {
                    setFocusedIndex((prev) => Math.max(prev - 1, -1));
                }
                break;
            case "Escape":
                setIsOpen(false);
                setFocusedIndex(-1);
                break;
        }
    }, [disabled, isOpen, focusedIndex, items, handleOptionClick]);
    useEffect(() => {
        const handleClick = (event) => {
            if (buttonRef.current?.contains(event.target)) {
                return;
            }
            if (dropdownRef.current &&
                !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setFocusedIndex(-1);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);
    return (_jsxs("div", { className: baseClassNames.container, onKeyDown: handleKeyDown, children: [_jsx(Button, { ref: buttonRef, variant: variant, disabled: disabled, onClick: handleButtonClick, className: "inline-flex items-center gap-2", rounded: rounded, ...props, children: children }), isOpen && (_jsx("div", { className: baseClassNames.dropdownContainer, children: _jsx("div", { ref: dropdownRef, className: baseClassNames.dropdown, children: _jsx("ul", { ref: listRef, className: baseClassNames.itemsList, role: "menu", children: items.map((item, index) => (_jsx("li", { role: "menuitem", onClick: () => handleOptionClick(item), onMouseEnter: () => setFocusedIndex(index), className: baseClassNames.item(false, focusedIndex === index), children: item.key }, item.value))) }) }) }))] }));
});
ButtonDropdown.displayName = "ButtonDropdown";
export default ButtonDropdown;
//# sourceMappingURL=ButtonDropdown.js.map